import { useEffect, useRef, useState } from 'react';
import logoNoBg from '../assets/logo-no-bg.png';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Alert from '../components/Alert';
import { Helmet } from 'react-helmet-async';
import TextInput from '../components/TextInput';
import { checkMarkBlackRegister, checkMarkGreenRegister } from '../domain/constants';
import Button from '../components/Button';
import Spinner from '../components/Spinner';
import { useAuthContext } from '../utils/Context';
import { classNames } from '../domain/helpers';
import { checkPasswordReq, togglePasswordVisibility } from '../utils/Helpers';
import TurnstileComponent from '../components/TurnstileComponent';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';

function Register() {
    const navigate = useNavigate();
    const authContext = useAuthContext((state) => state.auth);
    const formRef = useRef<HTMLFormElement>(null);

    const [alerts, setAlerts] = useState<AlertType>(null);
    const [showPassword, setShowPassword] = useState<boolean>(false);
    const [requestPending, setRequestPending] = useState<boolean>(false);
    const [turnstileToken, setTurnstileToken] = useState<string>('');
    const [passwordReq, setPasswordReq] = useState<RegisterPasswordRequirements[]>([
        {
            id: 'minLength',
            text: 'Min. 12 characters',
            isValid: false,
        },
        {
            id: 'minUppercase',
            text: '1 uppercase',
            isValid: false,
        },
        {
            id: 'minDigits',
            text: '1 digit',
            isValid: false,
        },
        {
            id: 'minSpecialCharacters',
            text: '1 special character',
            isValid: false,
        },
    ]);

    useEffect(() => {
        if (authContext.isLoggedIn) return navigate('/dashboard');
    }, []);

    const handleSubmit = (event: any) => {
        event.preventDefault();
        if (turnstileToken === '' || turnstileToken === undefined || turnstileToken === null) {
            return setAlerts({
                message: 'Please complete the captcha',
                alertType: 'error',
            });
        }
        if (passwordReq.some((item) => item.isValid === false))
            return setAlerts({ alertType: 'error', message: 'Password does not meet requirements.' });
        setRequestPending(true);

        const data: RegistrationForm = {
            firstName: event.target.firstName.value,
            lastName: event.target.lastName.value,
            email: event.target.email.value,
            password: event.target.password.value,
            organisationName: event.target.organisationName.value,
        };

        axios
            .post('/auth/register', { ...data, turnstileToken: turnstileToken })
            .then(() => {
                setAlerts({
                    alertType: 'success',
                    message: 'Account created successfully. Please activate your email.',
                });
                setRequestPending(false);
            })
            .catch((error) => {
                setAlerts({ alertType: 'warning', message: error.response.data.message });
                setRequestPending(false);
                //@ts-ignore
                turnstile.reset();
            });
    };

    return (
        <>
            <Helmet>
                <title>Register | Webbsly</title>
                <link rel="canonical" href={window.location.href} />
                <meta name="description" content="Register | Webbsly" />
            </Helmet>
            <div className="flex min-h-full flex-1 flex-col justify-center px-6 py-12 lg:px-8">
                <div className="sm:mx-auto sm:w-full sm:max-w-sm">
                    <img className="mx-auto h-20 w-auto" src={logoNoBg} alt="Webbsly" />
                    <h1 className="mt-10 text-center text-2xl font-bold leading-9 tracking-tight text-gray-900">
                        Create an account with Webbsly
                    </h1>
                </div>

                <div className="mt-10 sm:mx-auto sm:w-full sm:max-w-sm">
                    {alerts !== null && alerts.alertType === 'success' ? (
                        <Alert alertType={alerts.alertType} message={alerts.message} />
                    ) : (
                        <>
                            <form className="space-y-6" onSubmit={handleSubmit} ref={formRef}>
                                <div className="flex justify-between flex-col md:flex-row max-md:space-y-6">
                                    <div className="w-full md:w-[48%]">
                                        <label
                                            htmlFor="firstName"
                                            className="block text-sm font-medium leading-6 text-gray-900">
                                            First Name
                                        </label>
                                        <div className="mt-2">
                                            <TextInput
                                                id="firstName"
                                                name="firstName"
                                                type="text"
                                                autocomplete="given-name"
                                                placeholder="John"
                                                required={true}
                                            />
                                        </div>
                                    </div>
                                    <div className="w-full md:w-[48%]">
                                        <label
                                            htmlFor="lastName"
                                            className="block text-sm font-medium leading-6 text-gray-900">
                                            Last Name
                                        </label>
                                        <div className="mt-2">
                                            <TextInput
                                                id="lastName"
                                                name="lastName"
                                                type="text"
                                                autocomplete="family-name"
                                                placeholder="Doe"
                                                required={true}
                                            />
                                        </div>
                                    </div>
                                </div>
                                <div>
                                    <label
                                        htmlFor="organisationName"
                                        className="block text-sm font-medium leading-6 text-gray-900">
                                        Organisation Name
                                    </label>
                                    <div className="mt-2">
                                        <TextInput
                                            id="organisationName"
                                            name="organisationName"
                                            type="text"
                                            placeholder="My Organisation"
                                            required={true}
                                        />
                                    </div>
                                </div>
                                <div>
                                    <label
                                        htmlFor="email"
                                        className="block text-sm font-medium leading-6 text-gray-900">
                                        Email address
                                    </label>
                                    <div className="mt-2">
                                        <TextInput
                                            id="email"
                                            name="email"
                                            type="email"
                                            autocomplete="email"
                                            placeholder="john.doe@example.com"
                                            required={true}
                                        />
                                    </div>
                                </div>
                                <div>
                                    <div className="flex items-center justify-between">
                                        <label
                                            htmlFor="password"
                                            className="block text-sm font-medium leading-6 text-gray-900">
                                            Password
                                        </label>
                                    </div>
                                    <div className="mt-2 relative z-0">
                                        <TextInput
                                            id="password"
                                            name="password"
                                            type={showPassword ? 'text' : 'password'}
                                            autocomplete="password"
                                            required={true}
                                            // className='absolute z-1'
                                            onChange={(e) => checkPasswordReq(e.target.value, setPasswordReq)}
                                        />
                                        <button
                                            className="absolute inset-y-0 right-0 flex items-center pr-2"
                                            onClick={(e) => togglePasswordVisibility(e, setShowPassword)}
                                            tabIndex={-1}
                                            type="button">
                                            {showPassword ? (
                                                <EyeSlashIcon className="w-6 h-6 stroke-gray-600" />
                                            ) : (
                                                <EyeIcon className="w-6 h-6 stroke-gray-600" />
                                            )}
                                        </button>
                                    </div>
                                </div>
                                <div>
                                    <ul className="pl-6">
                                        {passwordReq.map((req, index) => (
                                            <li
                                                style={{
                                                    listStyleImage: `url('data:image/svg+xml;base64,${
                                                        req.isValid ? checkMarkGreenRegister : checkMarkBlackRegister
                                                    }')`,
                                                }}
                                                key={index}
                                                className={classNames(
                                                    'pl-2 text-sm',
                                                    req.isValid ? 'text-[#28b91c]' : 'text-gray-900',
                                                )}>
                                                {req.text}
                                            </li>
                                        ))}
                                    </ul>
                                </div>

                                <TurnstileComponent callback={setTurnstileToken} />

                                {requestPending && <Spinner className="w-full text-center" />}

                                {alerts && <Alert alertType={alerts.alertType} message={alerts.message} />}

                                <div>
                                    <Button disabled={requestPending} type="submit">
                                        Sign up
                                    </Button>
                                </div>
                            </form>

                            <p className="mt-10 text-center text-sm text-gray-500">
                                Already have an account?{' '}
                                <Link
                                    to="/login"
                                    onClick={(e) => requestPending && e.preventDefault()}
                                    className={classNames(
                                        'font-semibold leading-6 text-indigo-600 hover:text-indigo-500',
                                        requestPending ? 'opacity-50 cursor-not-allowed' : '',
                                    )}>
                                    Login
                                </Link>
                            </p>
                        </>
                    )}
                </div>
            </div>
        </>
    );
}

export default Register;
